import React from 'react'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import DataType from 'types/transformation/enums/DataType'

import { BOARD } from './accents'

interface ColumnLabelProps {
  name: string
  type?: DataType
}

/**
 * Returns data regarding a specific type, defaults to STRING
 */
const getTypeData = (type?: DataType) => {
  switch (type) {
  case DataType.BOOLEAN:
    return {
      icon: <Icon name="CheckBox" sx={{ fontSize: '12px' }} />,
      label: 'Sant/Falskt'
    }
  case DataType.DATE:
    return {
      icon: <Icon name="CalendarMonthOutlined" sx={{ fontSize: '12px' }} />,
      label: 'Datum'
    }
  case DataType.DOUBLE:
    return {
      icon: false,
      label: 'Decimaltal',
      altIcon: (
        <svg viewBox="0 0 10 10">
          <text
            x={'50%'}
            y={'50%'}
            textAnchor="middle"
            fontSize="26"
            fill={BOARD.textMuted}
            fontWeight={'400'}
            cursor="default"
          >
              ,
          </text>
        </svg>
      )
    }
  case DataType.BIGINT:
    return {
      icon: <Icon name="Tag" sx={{ fontSize: '12px' }} />,
      label: 'Heltal'
    }
  case DataType.TIMESTAMP:
    return {
      icon: <Icon name="DateRange" sx={{ fontSize: '12px' }} />,
      label: 'Tidsstämpel'
    }

  default:
    return {
      icon: <Icon name="TitleOutlined" sx={{ fontSize: '12px' }} />,
      label: 'Text'
    }
  }
}

/**
 * The small bordered box showing a column's data type (text, number, date …).
 * Shared by ColumnLabel and the board's field selectors so type iconography
 * stays consistent everywhere.
 */
export const TypeIcon = (props: { type?: DataType }) => {
  const { type } = props

  if (!type) {
    return null
  }

  const typeData = getTypeData(type)

  return (
    <Tooltip title={typeData.label}>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: 'common.white',
          border: `1px solid ${BOARD.border}`,
          borderRadius: '5px',
          color: BOARD.textMuted,
          display: 'flex',
          flex: 'none',
          height: '19px',
          justifyContent: 'center',
          width: '19px'
        }}
      >
        {typeData.icon ? typeData.icon : typeData.altIcon}
      </Box>
    </Tooltip>
  )
}

const ColumnLabel: React.FC<ColumnLabelProps> = ({ name, type }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        gap: '7px',
        minWidth: 0
      }}
    >
      <TypeIcon type={type} />
      <Text
        sx={{
          color: BOARD.textSecondary,
          fontFamily: BOARD.fontBody,
          fontSize: '12.5px',
          lineHeight: 1.5,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {name}
      </Text>
    </Box>
  )
}

export default ColumnLabel
